/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Stack, TextInput, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@fai/friendli-suite/features/i18n';
import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';

import { useRenameFinetuning } from './use-rename-finetuning';

const renameFinetuningModalId =
  'dedicated-endpoints-project-finetuning-detail-rename-finetuning-modal';

export const openRenameFinetuningModal = (props: { finetuningName: string }) => {
  modals.openContextModal({
    modal: 'footerless',
    modalId: renameFinetuningModalId,
    title: i18n.t('dedicated_endpoints.project.rename_finetuning'),
    innerProps: { modalBody: <RenameFinetuningModalBody {...props} /> },
  });
};

function RenameFinetuningModalBody({ finetuningName }: { finetuningName: string }) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [input, setInput] = useState<string>(finetuningName);

  const { renameFinetuning, loading: renameFinetuningLoading } = useRenameFinetuning({
    onCompleted: () => modals.close(renameFinetuningModalId),
  });

  return (
    <Stack gap={theme.pf.layout.xs}>
      <TextInput
        placeholder={`${t('dedicated_endpoints.project.enter_finetuning_name')}`}
        value={input}
        onChange={(e) => setInput(e.target.value)}
      />

      <FooterlessModalFooter>
        <Button variant="transparent" onClick={() => modals.close(renameFinetuningModalId)}>
          {t('shared.cancel')}
        </Button>

        <Button
          theme="cloud"
          disabled={input.length === 0}
          loading={renameFinetuningLoading}
          onClick={() => renameFinetuning(input)}
        >
          {t('shared.save')}
        </Button>
      </FooterlessModalFooter>
    </Stack>
  );
}

RenameFinetuningModalBody.displayName = 'RenameFinetuningModalBody';
